import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Linkedin, 
  Globe, 
  ArrowLeft,
  Quote,
  Calendar,
  Briefcase,
  GraduationCap,
  Trophy,
  Heart,
  Share2,
  Star,
  Award,
  Target,
  Clock
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { TeamMember } from '../../hooks/useTeamMembers';

const TeamMember = () => {
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState<Array<{ quote: string; author: string; role: string }>>([]);
  const [achievements, setAchievements] = useState([
    { icon: Calendar, label: '3+ Anos de Experiência' },
    { icon: Briefcase, label: '15+ Projetos Concluídos' },
    { icon: GraduationCap, label: 'Mestrado em Educação' },
    { icon: Trophy, label: '5 Prêmios de Excelência' }
  ]);

  const generateAITestimonials = async (memberInfo: TeamMember) => {
    try {
      const apiKey = import.meta.env.VITE_HUGGINGFACE_API_KEY;
      console.log('API Key disponível:', apiKey ? 'Sim' : 'Não');

      if (!apiKey) {
        throw new Error('API Key não encontrada');
      }

      const prompt = `
IMPORTANTE: Responda APENAS em português de Portugal.

Você é um assistente especializado em gerar testemunhos profissionais curtos.

Gere dois testemunhos profissionais sobre:
Nome: ${memberInfo.name}
Função: ${memberInfo.role}
Descrição: ${memberInfo.description}

As regras são as seguintes:
1. Retorne APENAS um objeto JSON com a seguinte estrutura exata:
{
  "testimonials": [
    {
      "quote": "Máximo de duas linhas curtas em português de Portugal",
      "author": "Nome real de professor do AEAAV",
      "role": "Cargo real no AEAAV"
    },
    {
      "quote": "Máximo de duas linhas curtas em português de Portugal",
      "author": "Outro nome real de professor do AEAAV",
      "role": "Outro cargo real no AEAAV"
    }
  ]
}

2. Cada testemunho deve ter NO MÁXIMO duas linhas curtas.
3. Use apenas nomes reais de professores do AEAAV (https://aeaav.pt).
4. Mencione competências específicas e o projeto Sinopse.
5. Não use linguagem repetitiva entre os testemunhos.
6. Use SEMPRE português de Portugal (não use português do Brasil).

IMPORTANTE: Não faça perguntas ou comentários. Apenas retorne o JSON conforme solicitado. Use APENAS a estrutura acima, sem exemplos ou explicações adicionais.
`;

      console.log('Enviando requisição para API...');

      const response = await fetch(
        "https://api-inference.huggingface.co/models/HuggingFaceH4/zephyr-7b-beta",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            inputs: prompt,
            parameters: {
              max_new_tokens: 300,
              temperature: 0.7,
              top_p: 0.95,
              do_sample: true,
              return_full_text: false
            }
          }),
        }
      );

      const responseText = await response.text();
      console.log('Resposta da API (testemunhos):', responseText);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText} - ${responseText}`);
      }

      try {
        const rawResponse = JSON.parse(responseText);
        let generatedText = rawResponse[0]?.generated_text || '';

        // Tentar encontrar o JSON na resposta
        let jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.error('Resposta completa:', generatedText);
          throw new Error('JSON não encontrado na resposta');
        }

        const result = JSON.parse(jsonMatch[0]);
        console.log('Resultado parseado:', result);

        if (!result.testimonials || result.testimonials.length !== 2) {
          throw new Error('Formato de resposta inválido');
        }

        console.log('Testemunhos processados:', result.testimonials);
        return result.testimonials;

      } catch (parseError) {
        console.error('Erro ao processar resposta dos testemunhos:', parseError);
        throw new Error('Formato de resposta inválido');
      }

    } catch (error) {
      console.error('Erro ao gerar testemunhos:', error);
      
      // Testemunhos padrão como fallback
      return [
        {
          quote: `A dedicação e profissionalismo de ${memberInfo.name} são verdadeiramente inspiradores no projeto Sinopse.`,
          author: "Dr. António Silva",
          role: "Diretor do AEAAV"
        },
        {
          quote: `O contributo de ${memberInfo.name} tem sido fundamental para elevar a qualidade técnica do nosso projeto.`,
          author: "Dra. Maria Santos",
          role: "Coordenadora Pedagógica"
        }
      ];
    }
  };

  const generateAchievements = async (memberInfo: TeamMember) => {
    try {
      const apiKey = import.meta.env.VITE_HUGGINGFACE_API_KEY;

      const prompt = `
IMPORTANTE: Responda APENAS em português de Portugal.

Gere 4 conquistas realistas em formato JSON para o seguinte perfil:
Nome: ${memberInfo.name}
Função: ${memberInfo.role}
Descrição: ${memberInfo.description}

As conquistas devem ser relacionadas ao projeto Sinopse e devem seguir estas regras:
1. Se for aluno, considere idade entre 15-17 anos.
2. Se for professor, considere experiência profissional.
3. Use SEMPRE português de Portugal (não use português do Brasil).
4. Retorne APENAS o JSON no seguinte formato:

{
  "achievements": [
    {
      "icon": "Trophy",
      "label": "Conquista 1"
    },
    {
      "icon": "Calendar",
      "label": "Conquista 2"
    },
    {
      "icon": "GraduationCap",
      "label": "Conquista 3"
    },
    {
      "icon": "Award",
      "label": "Conquista 4"
    }
  ]
}

IMPORTANTE: Não faça perguntas ou comentários. Apenas retorne o JSON conforme solicitado. Use APENAS os ícones: Trophy, Calendar, GraduationCap, Award. Não inclua perguntas, explicações ou texto adicional, apenas o JSON.
`;

      console.log('Enviando requisição para API...');

      const response = await fetch(
        "https://api-inference.huggingface.co/models/HuggingFaceH4/zephyr-7b-beta",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            inputs: prompt,
            parameters: {
              max_new_tokens: 500,
              temperature: 0.7,
              top_p: 0.95,
              do_sample: true,
              return_full_text: false
            }
          }),
        }
      );

      const responseText = await response.text();
      console.log('Resposta da API (achievements):', responseText);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText} - ${responseText}`);
      }

      try {
        const rawResponse = JSON.parse(responseText);
        let generatedText = rawResponse[0]?.generated_text || '';

        // Tentar encontrar o JSON na resposta
        let jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.error('Resposta completa:', generatedText);
          throw new Error('JSON não encontrado na resposta');
        }

        const result = JSON.parse(jsonMatch[0]);
        console.log('Resultado parseado:', result);

        if (!result.achievements || result.achievements.length !== 4) {
          throw new Error('Formato de resposta inválido');
        }

        // Mapear os ícones para os componentes reais
        const iconMap = {
          Trophy,
          Calendar,
          GraduationCap,
          Award
        };

        const processedAchievements = result.achievements.map(achievement => {
          const icon = iconMap[achievement.icon];
          if (!icon) {
            console.warn(`Ícone não encontrado: ${achievement.icon}, usando Trophy como fallback`);
            return { ...achievement, icon: Trophy };
          }
          return { ...achievement, icon };
        });

        console.log('Achievements processados:', processedAchievements);
        return processedAchievements;

      } catch (parseError) {
        console.error('Erro ao processar resposta dos achievements:', parseError);
        throw new Error('Formato de resposta inválido');
      }

    } catch (error) {
      console.error('Erro ao gerar achievements:', error);
      
      // Achievements padrão como fallback
      return [
        { icon: Trophy, label: '2 Anos de Dedicação' },
        { icon: Calendar, label: '5 Projetos Concluídos' },
        { icon: GraduationCap, label: 'Formação Contínua' },
        { icon: Award, label: 'Prémio Destaque 2023' }
      ];
    }
  };

  const formatDuration = (duration: string) => {
    // Remove qualquer texto adicional e mantenha apenas os números e ':'
    const timeMatch = duration.match(/(\d+:)?(\d+:\d+|\d+)/);
    if (!timeMatch) return duration;
    
    const timeParts = timeMatch[0].split(':');
    // Se tiver horas, converte para minutos
    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts;
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
      return `${totalMinutes}:${seconds}`;
    }
    // Se já estiver em minutos:segundos, retorna como está
    return timeMatch[0];
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_members')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        
        if (data) {
          setMember(data);
          // Gerar e atualizar os testemunhos e achievements
          const aiTestimonials = await generateAITestimonials(data);
          const aiAchievements = await generateAchievements(data);
          setTestimonials(aiTestimonials);
          setAchievements(aiAchievements);
        }
      } catch (err) {
        console.error('Error fetching team member:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMember();
    }
  }, [id]); // Dependência apenas no id

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Membro não encontrado</h1>
          <Link 
            to="/team" 
            className="text-primary hover:text-primary/80 transition-colors"
          >
            Voltar à equipa
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <div className="relative h-[70vh] overflow-hidden pt-16">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 bg-cover bg-[center_top_65%]"
          
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(${member.image})`,
            backgroundPosition: 'center 0% ',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/30 to-background" />
        
        <div className="relative h-full container mx-auto px-4 flex flex-col justify-end pb-20">
          <Link
            to="/team"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-primary transition-colors mb-8 w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar à equipa
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{member.name}</h1>
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-1 bg-primary/20 backdrop-blur-sm rounded-full text-primary">
                {member.role}
              </span>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: member.name,
                      text: `Conheça ${member.name}, ${member.role} no AEAAV`,
                      url: window.location.href
                    });
                  }
                }}
                className="p-2 hover:bg-secondary/80 rounded-full transition-colors"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-4">
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary/50 backdrop-blur-sm rounded-full hover:bg-secondary transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {member.email}
                </a>
              )}
              {member.linkedin && (
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-secondary/50 backdrop-blur-sm rounded-full hover:bg-secondary transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
              )}
              {member.website && (
                <a
                  href={member.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-secondary/50 backdrop-blur-sm rounded-full hover:bg-secondary transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  Website
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12">
            {/* About Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Sobre</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 leading-relaxed">
                  {member.description}
                </p>
              </div>
            </motion.div>

            {/* Achievements Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Conquistas</h2>
              <div className="grid grid-cols-2 gap-4">
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card p-4 rounded-lg hover:ring-1 hover:ring-primary/50 transition-all"
                  >
                    <achievement.icon className="w-8 h-8 text-primary mb-2" />
                    <p className="text-sm">{achievement.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Testimonials Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20"
          >
            <h2 className="text-3xl font-bold mb-12 text-center">
              O que dizem sobre {member?.name.split(' ')[0]}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-card p-8 rounded-lg relative group hover:ring-1 hover:ring-primary/50 transition-all"
                >
                  <Quote className="w-12 h-12 text-primary/20 absolute top-4 right-4" />
                  <p className="text-lg text-gray-300 mb-6 relative z-10">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-primary">{testimonial.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 text-center"
          >
            <div className="bg-card rounded-2xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
              <div className="relative z-10">
                <Heart className="w-12 h-12 text-primary mx-auto mb-6" />
                <h2 className="text-3xl font-bold mb-4">Trabalhe Conosco</h2>
                <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                  Junte-se à nossa equipa e faça parte de um projeto inovador e transformador na educação.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                >
                  Entrar em Contacto
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Estatísticas e Data */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="w-4 h-4 text-primary/70 shrink-0" />
              <span className="truncate">{formatDuration(member.duration || '')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMember;